<template>
  <div class="results-page-container">
    <!-- Full page background -->
    <div class="full-page-background">
      <img
        src="/img/homepage_bg.webp"
        alt="Background"
        class="background-image"
      />
    </div>

    <!-- Page content overlay -->
    <div class="page-content">
      <!-- Header -->
      <div class="page-header">
        <h1 class="page-title">Your Results</h1>
        <p class="page-subtitle" v-if="winningMethods.length > 0">
          {{ dynamicResultText }}
        </p>
      </div>

      <!-- Results content -->
      <div class="results-area" v-if="winningMethods.length > 0">
        <!-- Method cards -->
        <div
          v-for="(method, idx) in winningMethodsDetails"
          :key="method.id"
          class="method-card"
          :class="{ 'method-card-static': method.removeFromDetailsPage }"
          :style="{ borderColor: method.color || '#3D80E8' }"
          :role="method.removeFromDetailsPage ? null : 'button'"
          :tabindex="method.removeFromDetailsPage ? null : 0"
          :aria-label="
            method.removeFromDetailsPage
              ? null
              : 'View details for ' + getMethodDisplayName(method)
          "
          @click="
            !method.removeFromDetailsPage && goToMethodDetailsPage(method.id)
          "
          @keydown.enter="
            !method.removeFromDetailsPage && goToMethodDetailsPage(method.id)
          "
          @keydown.space.prevent="
            !method.removeFromDetailsPage && goToMethodDetailsPage(method.id)
          "
        >
          <div class="method-card-header">
            <div class="method-header-content">
              <div
                class="method-icon-chip"
                :style="{
                  background: hexToRgba(method.color || '#3D80E8', 0.14),
                }"
              >
                <img
                  :src="method.imageUrl"
                  :alt="method.name"
                  class="method-icon"
                />
              </div>
              <div class="method-title-wrap">
                <span
                  v-if="idx === 0 && winningMethodsDetails.length > 1"
                  class="top-match-badge"
                  >Top Match</span
                >
                <h3
                  class="method-title"
                  :style="{ color: method.color || '#3D80E8' }"
                >
                  {{ getMethodDisplayName(method) }}
                </h3>
              </div>
            </div>
          </div>
          <div class="method-card-content">
            <p v-html="method.resultText"></p>
          </div>
          <span
            v-if="!method.removeFromDetailsPage"
            class="learn-more"
            :style="{ color: method.color || '#3D80E8' }"
            aria-hidden="true"
            >Learn More →</span
          >
        </div>
      </div>

      <!-- No results state -->
      <div v-else class="no-results">
        <h3>No suitable methods found</h3>
        <p>
          Based on your selection, we could not find a combination of methods
          that could work for you.
        </p>
      </div>

      <div class="results-disclaimer">
        <p>
          ⚠️ This quiz is for information only and does not replace medical
          advice. Please speak to a healthcare provider before starting any HIV
          prevention method.
        </p>
      </div>

      <div class="retake-wrap">
        <button type="button" class="retake-button" @click="goQuizPageOne">
          Retake Quiz
        </button>
      </div>
    </div>

    <!-- Fixed bottom buttons -->
    <div class="fixed-bottom-buttons">
      <v-app-bar location="bottom" class="bottom-bar">
        <div class="results-footer-row">
          <v-btn
            class="back-button"
            size="large"
            @click="goQuizPageOne"
            variant="elevated"
          >
            BACK
          </v-btn>
          <v-btn
            class="action-btn find-clinic-btn"
            size="large"
            @click="navigateToServiceFinder"
            variant="elevated"
          >
            FIND A CLINIC
          </v-btn>
          <v-btn
            class="action-btn chat-btn"
            size="large"
            @click="navigateToWhatsapp"
            variant="elevated"
          >
            <v-icon left>mdi-whatsapp</v-icon>
            CHAT
          </v-btn>
        </div>
      </v-app-bar>
    </div>
  </div>
</template>

<script>
import MethodDataService from "../services/MethodDataService";
import { mapGetters } from "vuex";

export default {
  name: "QuizResultsPage",
  data: () => ({
    allMethods: [],
  }),
  async created() {
    // Get all methods from database
    this.allMethods = await MethodDataService.getAll();

    console.log("Quiz Results from Store:", this.QuizResults);
    console.log("All Methods:", this.allMethods);
  },
  computed: {
    ...mapGetters(["country", "QuizResults"]),
    winningMethods() {
      return this.QuizResults.winningMethods || [];
    },
    selectedChoices() {
      return this.QuizResults.selectedChoices || [];
    },
    methodScores() {
      return this.QuizResults.methodScores || {};
    },
    winningMethodsDetails() {
      // Get full method details for winning methods
      return this.winningMethods
        .map((methodId) =>
          this.allMethods.find((method) => method.id === methodId),
        )
        .filter((method) => method) // Remove any undefined methods
        .filter((method) => {
          // Filter by country if applicable
          if (
            method.applicableCountries &&
            method.applicableCountries.length > 0
          ) {
            return method.applicableCountries.includes(this.country);
          }
          return true; // Include methods with no country restrictions
        });
    },
    dynamicResultText() {
      if (this.winningMethods.length === 0) {
        return "";
      }

      return this.winningMethodsDetails.length === 1
        ? "Based on your answers, this method may work best for you:"
        : "Based on your answers, these methods may work for you:";
    },
  },
  methods: {
    getMethodDisplayName(method) {
      if (!method) return "";
      // Injections aren't grouped — each shows its own specific name
      // (e.g. "CAB-LA Injection" vs "Lenacapavir (LEN)") rather than a
      // generic "PrEP Injection" label.
      if (method.methodType === "Injections") {
        return method.name;
      }
      const displayNames = {
        Pills: "Oral PrEP",
        Rings: "PrEP Ring",
      };
      return displayNames[method.methodType] || method.methodType;
    },
    goToMethodDetailsPage(methodId) {
      const method = this.allMethods.find((m) => m.id === methodId);
      const methodName = method ? method.name : "Unknown Method";

      this.$gtag.event("Navigation to Details Page", {
        event_category: "navigation_to_details_page",
        event_label: "navigation_to_details_page_from_quiz_results",
        value: methodName,
      });

      this.$router.push({
        name: "Method Details",
        params: { choice: methodId },
      });
    },
    goQuizPageOne() {
      this.$router.push({
        name: "QuizPageOne",
      });
    },
    hexToRgba(hex, alpha) {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },
    navigateToServiceFinder() {
      this.$gtag.event("Navigation to Service Finder", {
        event_category: "navigation_to_service_finder",
        event_label: "navigation_to_service_finder_from_quiz_results_page",
        value: "quiz_results_page",
      });
      window.open(
        "https://witsrhi-servicefinder-client.onrender.com/",
        "_blank",
      );
    },
    navigateToWhatsapp() {
      this.$gtag.event("Navigation to Talk to Somebody", {
        event_category: "navigation_to_talk_to_somebody",
        event_label: "navigation_to_talk_to_somebody_from_quiz_results_page",
        value: "quiz_results_page",
      });
      window.open("https://api.whatsapp.com/send?phone=27781680192", "_blank");
    },
  },
};
</script>

<style lang="scss" scoped>
// QuizResultsPage with full-page background

.results-page-container {
  height: 100vh;
  position: relative;
  overflow-y: scroll; // Always reserve the scrollbar gutter so content width stays stable
  overflow-x: hidden;
  background: transparent;
  scrollbar-width: thin; // Firefox — force a persistent, non-overlay scrollbar
  scrollbar-color: rgba(255, 255, 255, 0.7) rgba(255, 255, 255, 0.15);

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.15);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.7);
    border-radius: 4px;
  }
}

.full-page-background {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
}

.background-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 30%;
}

.page-content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  padding: 0;
  padding-bottom: 120px; // Space for fixed bottom button
}

.page-header {
  padding: 40px 24px 20px 24px;
  text-align: center;
  flex-shrink: 0;

  @media (max-height: 480px) and (orientation: landscape) {
    padding: 16px 24px 12px 24px;
  }
}

.page-title {
  color: white;
  font-size: 32px;
  font-weight: 900;
  font-family: "Montserrat", sans-serif;
  text-align: center;
  margin: 0 0 20px 0;
  line-height: 1;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.page-subtitle {
  color: white;
  font-size: 16px;
  font-weight: 500;
  font-family: "Montserrat", sans-serif;
  margin: 0;
  line-height: 1.3;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.results-area {
  flex: 1;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
}

.method-card {
  width: 100%;
  max-width: 680px;
  margin: 0 auto 16px;
  background: #ffffff;
  border: 3px solid #3d80e8;
  border-radius: 20px;
  padding: 20px;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 3px solid #eba614;
    outline-offset: 2px;
  }
}

.method-card-static {
  cursor: default;

  &:hover {
    transform: none;
  }
}

.method-card-header {
  margin-bottom: 12px;
}

.method-header-content {
  display: flex;
  align-items: center;
  gap: 14px;
}

.method-icon-chip {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.method-icon {
  width: 32px;
  height: 32px;
  object-fit: contain;
  flex-shrink: 0;
}

.method-title-wrap {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.top-match-badge {
  align-self: flex-start;
  background: #eba614;
  color: #ffffff;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 2px 10px;
  border-radius: 99px;
}

.method-title {
  font-size: 18px;
  font-weight: 700;
  font-family: "Montserrat", sans-serif;
  margin: 0;
  flex: 1;
}

.method-card-content {
  color: #333;
  font-size: 14px;
  line-height: 1.5;
  font-family: "Montserrat", sans-serif;
  margin-bottom: 12px;

  :deep(strong) {
    font-weight: 600;
  }

  :deep(p) {
    margin: 0;
  }
}

.learn-more {
  display: inline-block;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.results-footer-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-around;
  gap: 8px;
  width: 100%;
}

.action-btn {
  font-weight: 700 !important;
  font-size: 12px !important;
  text-transform: none !important;
  border-radius: 10px !important;
  height: 50px !important;
  min-width: 100px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) !important;

  @media (max-width: 360px) {
    min-width: 84px !important;
    padding: 0 10px !important;
  }
}

.find-clinic-btn {
  background: #e38fe5 !important;
  color: white !important;
}

.chat-btn {
  background: #05bf99 !important;
  color: white !important;
}

.no-results {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 40px 24px;
  text-align: center;
  color: white;

  h3 {
    font-size: 20px;
    font-weight: 700;
    margin-bottom: 16px;
    font-family: "Montserrat", sans-serif;
  }

  p {
    font-size: 16px;
    line-height: 1.4;
    font-family: "Montserrat", sans-serif;
  }
}

.results-disclaimer {
  width: 100%;
  max-width: 680px;
  margin: 8px auto 0;
  padding: 16px 20px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 14px;
  box-sizing: border-box;

  p {
    margin: 0;
    color: #555;
    font-size: 14px;
    line-height: 1.6;
    text-align: center;
    font-family: "Montserrat", sans-serif;
  }
}

.retake-wrap {
  display: flex;
  justify-content: center;
  padding: 20px 24px 0;
}

.retake-button {
  background: transparent;
  color: #ffffff;
  border: 2px solid rgba(255, 255, 255, 0.7);
  border-radius: 99px;
  padding: 10px 28px;
  font-weight: 700;
  font-size: 0.95rem;
  font-family: "Montserrat", sans-serif;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
}

.fixed-bottom-buttons {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
}

.bottom-bar {
  border: none !important;
}

.back-button {
  background: #eb63eb !important;
  color: white !important;
  border-radius: 10px !important;
  font-weight: bold !important;
  font-size: 12px !important;
  text-transform: none !important;
  padding: 0 16px !important;
  height: 50px !important;
  min-width: 100px !important;
  box-shadow: 0 4px 12px rgba(235, 99, 235, 0.3) !important;

  @media (max-width: 360px) {
    min-width: 84px !important;
    padding: 0 10px !important;
  }
}

// Override global styles for this page — transparent bottom bar, matching HomePage
:deep(.v-app-bar) {
  background: transparent !important;
  box-shadow: none !important;
}
</style>
